import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
	type ReactNode,
	Fragment,
} from 'react';

import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { PreviewPanelContent } from './types.tsx';

interface PreviewPanelContextType {
	isOpen: boolean;
	width: number;
	content?: PreviewPanelContent;
	openPreviewPanel: (content?: PreviewPanelContent) => void;
	closePreviewPanel: () => void;
	setWidth: (width: number) => void;
}

const PreviewPanelContext = createContext<PreviewPanelContextType | undefined>(undefined);

interface PreviewPanelProviderProps {
	children: ReactNode;
	defaultWidth?: number;
	onOpen?: () => void;
	onClose?: () => void;
	onWidthChange?: (width: number) => void;
}

const PreviewPanelProviderBase: React.FC<PreviewPanelProviderProps> = ({
	children,
	defaultWidth = 400,
	onOpen,
	onClose,
	onWidthChange,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [width, setWidth] = useState(defaultWidth);
	const [content, setContent] = useState<PreviewPanelContent>();

	const openPreviewPanel = useCallback(
		(newContent?: PreviewPanelContent) => {
			setIsOpen(true);
			setContent(newContent);
			onOpen?.();
		},
		[onOpen],
	);

	const closePreviewPanel = useCallback(() => {
		setIsOpen(false);
		onClose?.();
	}, [onClose]);

	const handleWidthChange = useCallback(
		(newWidth: number) => {
			setWidth(newWidth);
			onWidthChange?.(newWidth);
		},
		[onWidthChange],
	);

	const value = useMemo(
		() => ({
			isOpen,
			width,
			content,
			openPreviewPanel,
			closePreviewPanel,
			setWidth: handleWidthChange,
		}),
		[isOpen, width, content, openPreviewPanel, closePreviewPanel, handleWidthChange],
	);

	return <PreviewPanelContext.Provider value={value}>{children}</PreviewPanelContext.Provider>;
};

export const usePreviewPanel = () => {
	const context = useContext(PreviewPanelContext);
	if (context === undefined) {
		throw new Error('usePreviewPanel must be used within a PreviewPanelProvider');
	}
	return context;
};

const PreviewPanelProvider = componentWithFG(
	'jira-glance-panel',
	PreviewPanelProviderBase,
	Fragment,
);

export default PreviewPanelProvider;
